import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
  Paper,
} from "@mui/material";
import Logger from "../../utils/logger";
import { fetchPromptPreview } from "../../services/api";
import {
  PromptPreviewCategory,
  PromptPreviewClaimType,
  PromptPreviewResponse,
  PROMPT_PREVIEW_CATEGORY_LABELS,
  PROMPT_PREVIEW_CLAIM_TYPE_LABELS,
} from "../../types";

const PROMPT_PREVIEW_CATEGORIES: PromptPreviewCategory[] = [
  "preference",
  "claim",
  "claimWithSourceText",
];

const PROMPT_PREVIEW_CLAIM_TYPES: PromptPreviewClaimType[] = ["fact", "value", "policy"];

// system / user プロンプト本文を等幅フォントでそのまま表示する
const PromptTextBlock: React.FC<{ label: string; text: string }> = ({ label, text }) => (
  <Box sx={{ mt: 2 }}>
    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
      {label}
      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
        {text.length}文字
      </Typography>
    </Typography>
    <Paper variant="outlined" sx={{ p: 2, bgcolor: "action.hover" }}>
      <Box
        component="pre"
        sx={{
          m: 0,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          fontFamily: "monospace",
          fontSize: "0.8rem",
          lineHeight: 1.6,
        }}
      >
        {text}
      </Box>
    </Paper>
  </Box>
);

const PromptPreview: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<PromptPreviewCategory>("preference");
  const [selectedClaimType, setSelectedClaimType] = useState<PromptPreviewClaimType>("value");
  const [promptPreview, setPromptPreview] = useState<PromptPreviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isClaimCategory = selectedCategory !== "preference";

  useEffect(() => {
    const loadPromptPreview = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchPromptPreview(
          selectedCategory,
          selectedCategory === "preference" ? undefined : selectedClaimType
        );
        setPromptPreview(response);
      } catch (fetchError) {
        Logger.error("Failed to fetch prompt preview:", fetchError);
        setError("プロンプトの取得に失敗しました");
      } finally {
        setLoading(false);
      }
    };
    loadPromptPreview();
  }, [selectedCategory, selectedClaimType]);

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            プロンプト確認
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            実際の評価で使われるプロンプトビルダー（v2.1.0）が組み立てたプロンプトを表示します。
            （〜）の部分は実際の評価時にお題やユーザーの入力で置き換わります。意見文の文字数指定と文体指示はサンプル文から算出されるため、実際の評価時とは異なります。
          </Typography>
          {promptPreview && (
            <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
              <Chip
                label={`evaluationVersion: ${promptPreview.evaluationVersion}`}
                size="small"
                color="primary"
                variant="outlined"
              />
              <Chip
                label={`promptVersion: ${promptPreview.promptVersion}`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Tabs
            value={PROMPT_PREVIEW_CATEGORIES.indexOf(selectedCategory)}
            onChange={(_event, nextIndex: number) =>
              setSelectedCategory(PROMPT_PREVIEW_CATEGORIES[nextIndex])
            }
            variant="scrollable"
            scrollButtons="auto"
          >
            {PROMPT_PREVIEW_CATEGORIES.map((category) => (
              <Tab key={category} label={PROMPT_PREVIEW_CATEGORY_LABELS[category]} />
            ))}
          </Tabs>

          {isClaimCategory && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                claimType（主張のサブタイプ）
              </Typography>
              <ToggleButtonGroup
                value={selectedClaimType}
                exclusive
                onChange={(_event, nextClaimType: PromptPreviewClaimType | null) => {
                  if (nextClaimType !== null) {
                    setSelectedClaimType(nextClaimType);
                  }
                }}
                size="small"
              >
                {PROMPT_PREVIEW_CLAIM_TYPES.map((claimType) => (
                  <ToggleButton key={claimType} value={claimType}>
                    {PROMPT_PREVIEW_CLAIM_TYPE_LABELS[claimType]}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          )}
        </CardContent>
      </Card>

      {loading && (
        <Box display="flex" justifyContent="center" mt={6}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {!loading &&
        !error &&
        promptPreview?.prompts.map((axisPrompt) => (
          <Card key={axisPrompt.axis} variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {axisPrompt.label}
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  {axisPrompt.axis}
                </Typography>
              </Typography>
              <PromptTextBlock label="system" text={axisPrompt.prompt.system} />
              <PromptTextBlock label="user" text={axisPrompt.prompt.user} />
            </CardContent>
          </Card>
        ))}
    </Box>
  );
};

export default PromptPreview;
